import { useAppQuery } from 'core/query/hooks/useAppQuery';
import {
  filterIntegrationsByTenantApi,
  FilterIntegrationsByTenantResult,
} from 'IntegrationModule/api/filterIntegrationsByTenantApi';
import { integrationQueryKeys } from './integrationQueryKeys';
import { integrationsHash } from '@constants/integration';
import { AxiosResponse } from 'axios';
import ApiClientManager from 'core/apiClient';

/**
 * Source of truth: IS backend ServiceName enum
 * (integrations-server/src/integrations/constants/service-names.ts)
 *
 * Exported as the draft-capable service list: the Create Draft modal only
 * ever queries these names, so UI offering "add a service, then draft it"
 * (AddServiceToRealmSection) must not offer anything outside this list.
 *
 * jira-cloud is deliberately absent: jira-agent is retired, so no new Jira drafts.
 */
export const DRAFT_CAPABLE_SERVICE_NAMES = [
  'asana',
  'bqe-core',
  'bst-hosted',
  'csv-agent',
  'deltek-ajera-cloud',
  'deltek-ajera-hosted',
  'deltek-costpoint-odbc',
  'deltek-pim-hosted',
  'deltek-vantagepoint-cloud',
  'deltek-vantagepoint-hosted',
  'deltek-vision-cloud',
  'deltek-vision-hosted',
  'quickbooks-desktop',
  'quickbooks-online-new',
  'unanet-ae',
  'unanet-gov',
  'viewpoint-vista-hosted',
  'mock-agent',
];

export type TenantServiceOption = {
  serviceName: string;
  label: string;
  onRealm: boolean;
};

const mapToServiceOptions = (results: FilterIntegrationsByTenantResult[]): TenantServiceOption[] =>
  results.map(({ name, onRealm }) => ({
    serviceName: name,
    label: integrationsHash[name]?.label ?? name,
    onRealm,
  }));

/**
 * Exported so mutations that change the realm's services (e.g. the Add
 * Service To Realm section) can invalidate exactly this query instead of
 * every integration list.
 */
export const filterIntegrationsByTenantQueryKey = ({
  tenant,
  realmId,
}: { tenant?: string; realmId?: string } = {}) =>
  integrationQueryKeys.list({
    identifier: 'useFilterIntegrationsByTenantQuery',
    tenant,
    realmId,
  });

export const useFilterIntegrationsByTenantQuery = ({
  tenant,
  realmId,
}: { tenant?: string; realmId?: string } = {}) => {
  const query = useAppQuery<AxiosResponse<FilterIntegrationsByTenantResult[]>>({
    queryKey: filterIntegrationsByTenantQueryKey({ tenant, realmId }),
    queryFn: ({ token, buildConfig }) =>
      filterIntegrationsByTenantApi({
        params: { integrations: DRAFT_CAPABLE_SERVICE_NAMES },
        token,
        // Filter against the viewed team's realm, not the logged-in admin's
        // default context (buildUrlHeaders falls back per-field when omitted).
        config: buildConfig({
          headers: ApiClientManager.buildUrlHeaders({
            tenantName: tenant,
            realmId,
          }),
        }),
      }),
  });

  const serviceOptions: TenantServiceOption[] = query.data?.data
    ? mapToServiceOptions(query.data.data)
    : [];

  return {
    serviceOptions,
    isLoadingServices: query.isLoading,
    isErrorServices: query.isError,
  };
};
